import {
    AlertTriangle,
    ArrowRight,
    CalendarDays,
    Check,
    ChevronLeft,
    CircleX,
    Info,
    LoaderCircle,
    Search,
    Users,
    WandSparkles,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './InitializeLeaveBalancesScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const normalizeStaff = (payload) => {
    const candidates = [
        payload?.staff,
        payload?.staffReadiness,
        payload?.staffList,
        payload?.response,
    ]
    const array = candidates.find(Array.isArray) || []

    return array.map((item, index) => ({
        staffIdentifier: item?.staffIdentifier || item?.employeeIdentifier || item?.id || `staff-${index}`,
        staffName: item?.staffName || item?.name || item?.fullName || 'Unnamed Staff',
        staffCode: item?.staffCode || item?.employeeCode || '',
    }))
}

const normalizePreselectedStaff = (preselectedStaff = []) => {
    if (!Array.isArray(preselectedStaff)) return []
    return preselectedStaff
        .map((item, index) => {
            if (typeof item === 'string') {
                return {
                    staffIdentifier: item,
                    staffName: `Staff ${index + 1}`,
                    staffCode: '',
                }
            }
            return {
                staffIdentifier: item?.staffIdentifier || item?.employeeIdentifier || item?.id || '',
                staffName: item?.staffName || item?.name || item?.fullName || `Staff ${index + 1}`,
                staffCode: item?.staffCode || item?.employeeCode || '',
            }
        })
        .filter((item) => item.staffIdentifier)
}

const buildYearOptions = (baseYear) => [baseYear - 1, baseYear, baseYear + 1]

const renderBalanceSummary = (balances) => {
    if (!balances || typeof balances !== 'object') return ''
    return Object.entries(balances)
        .map(([leaveType, value]) => `${leaveType}: ${Number(value?.openingBalance || 0).toFixed(1)}d`)
        .join(' · ')
}

const InitializeLeaveBalancesScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const initialPreselectedStaff = useMemo(
        () => normalizePreselectedStaff(location?.state?.preselectedStaff || []),
        [location?.state?.preselectedStaff]
    )

    const currentYear = new Date().getFullYear()
    const [year, setYear] = useState(currentYear)
    const [initializeAll, setInitializeAll] = useState(initialPreselectedStaff.length === 0)
    const [selectedStaff, setSelectedStaff] = useState(initialPreselectedStaff)
    const [availableStaff, setAvailableStaff] = useState(initialPreselectedStaff)
    const [searchQuery, setSearchQuery] = useState('')
    const [isLoadingStaff, setIsLoadingStaff] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [results, setResults] = useState(null)

    const yearOptions = useMemo(() => buildYearOptions(currentYear), [currentYear])

    const selectedMap = useMemo(() => {
        const map = new Map()
        selectedStaff.forEach((staff) => map.set(staff.staffIdentifier, staff))
        return map
    }, [selectedStaff])

    const filteredStaff = useMemo(() => {
        const query = searchQuery.trim().toLowerCase()
        if (!query) return availableStaff
        return availableStaff.filter((staff) => {
            const name = String(staff.staffName || '').toLowerCase()
            const code = String(staff.staffCode || '').toLowerCase()
            const identifier = String(staff.staffIdentifier || '').toLowerCase()
            return name.includes(query) || code.includes(query) || identifier.includes(query)
        })
    }, [availableStaff, searchQuery])

    const fetchStaff = useCallback(async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) {
            setIsLoadingStaff(false)
            return
        }

        try {
            setIsLoadingStaff(true)
            const response = await fetch(BASE_URL_API + 'getPayrollStaffReadiness', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                }),
            })

            if (!response.ok) {
                toast.error('Unable to load staff list.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                const normalized = normalizeStaff(respo?.response || {})
                setAvailableStaff((prev) => {
                    const existing = new Map(prev.map((item) => [item.staffIdentifier, item]))
                    normalized.forEach((item) => existing.set(item.staffIdentifier, item))
                    return Array.from(existing.values())
                })
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            toast.error(respo?.message || 'Unable to load staff list.')
        } catch (error) {
            console.error('getPayrollStaffReadiness error:', error)
            toast.error('Unable to load staff list.')
        } finally {
            setIsLoadingStaff(false)
        }
    }, [activeStore?.storeIdentifier, authCtx])

    useEffect(() => {
        fetchStaff()
    }, [fetchStaff])

    const toggleStaff = (staff) => {
        setSelectedStaff((prev) => {
            const exists = prev.some((item) => item.staffIdentifier === staff.staffIdentifier)
            if (exists) return prev.filter((item) => item.staffIdentifier !== staff.staffIdentifier)
            return [...prev, staff]
        })
    }

    const initializeLeaveBalances = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) {
            toast.error('Missing authentication or store information.')
            return
        }

        if (!initializeAll && selectedStaff.length === 0) {
            toast.error('Select at least one staff member or enable Initialize All.')
            return
        }

        try {
            setIsSaving(true)
            setResults(null)

            const payload = {
                token: authCtx.token,
                storeIdentifier: activeStore.storeIdentifier,
                requestedBy: authCtx?.authIdentifier || '',
                year,
                initializeAll,
            }

            if (!initializeAll) {
                payload.staffIdentifiers = selectedStaff.map((item) => item.staffIdentifier)
            }

            const response = await fetch(BASE_URL_API + 'initializeStaffLeaveBalances', {
                method: 'POST',
                body: JSON.stringify(payload),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success' || Array.isArray(respo?.response?.results)) {
                const payloadResponse = respo?.response || {}
                setResults(payloadResponse)

                const totalSuccess = Number(payloadResponse?.totalSuccess || 0)
                const totalSkipped = Number(payloadResponse?.totalSkipped || 0)

                if (totalSuccess > 0) {
                    toast.success(
                        `Leave balances initialized for ${totalSuccess} staff.` +
                            (totalSkipped > 0 ? ` ${totalSkipped} staff already had balances — skipped.` : '')
                    )
                } else {
                    toast.info('All selected staff already had balances initialized.')
                }
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'LEAVE_002':
                    toast.error('No leave policies found. Configure leave policies first.')
                    navigate('/payroll/setup/leave-policy/setup')
                    break
                case 'VAL_001':
                case 'VAL_002':
                case 'VAL_007':
                    toast.error(respo?.message || 'Initialization failed.')
                    break
                default:
                    toast.error(respo?.message || 'Initialization failed.')
                    break
            }
        } catch (error) {
            console.error('initializeStaffLeaveBalances error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    const onDone = () => {
        navigate(-1)
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={onDone} type="button">
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <div className={styles.heroPill}>
                        <WandSparkles size={12} />
                        Leave Setup
                    </div>
                    <h1 className={styles.headerTitle}>Initialize Leave Balances</h1>
                    <p className={styles.headerSub}>Set up leave balances for staff for {year}</p>
                </div>
            </div>

            <div className={styles.sectionCard}>
                <div className={styles.sectionHead}>
                    <CalendarDays size={14} />
                    YEAR
                </div>
                <div className={styles.segmentBtnGroup}>
                    {yearOptions.map((option) => (
                        <button
                            type="button"
                            key={option}
                            className={`${styles.segmentBtn} ${year === option ? styles.segmentBtnActive : ''}`}
                            onClick={() => setYear(option)}
                        >
                            {option}
                        </button>
                    ))}
                </div>
            </div>

            <div className={styles.sectionCard}>
                <div className={styles.sectionHead}>
                    <Users size={14} />
                    SCOPE
                </div>
                <div className={styles.segmentBtnGroup}>
                    <button
                        type="button"
                        className={`${styles.segmentBtn} ${initializeAll ? styles.segmentBtnActive : ''}`}
                        onClick={() => setInitializeAll(true)}
                    >
                        All Staff
                    </button>
                    <button
                        type="button"
                        className={`${styles.segmentBtn} ${!initializeAll ? styles.segmentBtnActive : ''}`}
                        onClick={() => setInitializeAll(false)}
                    >
                        Select Staff
                    </button>
                </div>
            </div>

            {!initializeAll && (
                <div className={styles.sectionCard}>
                    <div className={styles.sectionHead}>
                        <Search size={14} />
                        STAFF SELECTOR
                    </div>

                    <div className={styles.searchWrap}>
                        <Search size={14} />
                        <input
                            className={styles.searchInput}
                            placeholder="Search by name, code, or identifier"
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                        />
                    </div>

                    {isLoadingStaff ? (
                        <div className={styles.stateRow}>
                            <LoaderCircle size={14} className={styles.spinIcon} />
                            <span>Loading staff...</span>
                        </div>
                    ) : filteredStaff.length === 0 ? (
                        <div className={styles.stateRow}>
                            <Info size={14} />
                            <span>No staff matched your search.</span>
                        </div>
                    ) : (
                        <div className={styles.staffList}>
                            {filteredStaff.map((staff) => {
                                const isSelected = selectedMap.has(staff.staffIdentifier)
                                return (
                                    <button
                                        key={staff.staffIdentifier}
                                        type="button"
                                        className={`${styles.staffRow} ${
                                            isSelected ? styles.staffRowActive : ''
                                        }`}
                                        onClick={() => toggleStaff(staff)}
                                    >
                                        <div className={styles.staffMeta}>
                                            <p className={styles.staffName}>{staff.staffName}</p>
                                            <p className={styles.staffCode}>
                                                {staff.staffCode || staff.staffIdentifier}
                                            </p>
                                        </div>
                                        {isSelected ? <Check size={16} /> : null}
                                    </button>
                                )
                            })}
                        </div>
                    )}
                </div>
            )}

            <div className={styles.infoCard}>
                <AlertTriangle size={15} />
                <p>
                    Staff who joined after January 1, {year} will have their CL and SL entitlements pro-rated to
                    their joining month. PL starts at 0 and accrues monthly.
                </p>
            </div>

            <button
                type="button"
                className={styles.saveBtn}
                onClick={initializeLeaveBalances}
                disabled={isSaving}
            >
                {isSaving ? (
                    <>
                        <LoaderCircle size={16} className={styles.spinIcon} />
                        Initializing...
                    </>
                ) : (
                    <>
                        Initialize Leave Balances
                        <ArrowRight size={16} />
                    </>
                )}
            </button>

            {results && (
                <div className={styles.resultsContainer}>
                    <p className={styles.resultsTitle}>
                        {Number(results?.totalSuccess || 0)} initialized · {Number(results?.totalSkipped || 0)} skipped
                        {Number(results?.totalFailed || 0) > 0
                            ? ` · ${Number(results?.totalFailed || 0)} failed`
                            : ''}
                    </p>

                    <div className={styles.resultsList}>
                        {(Array.isArray(results?.results) ? results.results : []).map((result, index) => {
                            const status = String(result?.status || '').toLowerCase()
                            return (
                                <div key={`${result?.staffIdentifier || index}`} className={styles.resultRow}>
                                    <div className={styles.resultIconWrap}>
                                        {status === 'success' ? (
                                            <Check size={14} className={styles.successIcon} />
                                        ) : status === 'skipped' ? (
                                            <Info size={14} className={styles.skippedIcon} />
                                        ) : (
                                            <CircleX size={14} className={styles.failedIcon} />
                                        )}
                                    </div>
                                    <div className={styles.resultInfo}>
                                        <p className={styles.resultName}>{result?.staffName || 'Unnamed Staff'}</p>
                                        {status === 'success' ? (
                                            <>
                                                {Boolean(result?.isProRated) && (
                                                    <p className={styles.proRatedNote}>
                                                        Pro-rated (joined month {result?.joiningMonth || '—'})
                                                    </p>
                                                )}
                                                <p className={styles.balanceSummary}>
                                                    {renderBalanceSummary(result?.balances) || 'Balances initialized'}
                                                </p>
                                            </>
                                        ) : (
                                            <p className={styles.resultNote}>
                                                {result?.message || 'Skipped due to existing balances.'}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <button type="button" className={styles.doneBtn} onClick={onDone}>
                        Done
                    </button>
                </div>
            )}
        </div>
    )
}

export default InitializeLeaveBalancesScreen